import { z } from 'zod';
import type { DomainEventType } from './events.js';

/**
 * Trigger contract: what fires a trigger (spec per type) and what task it creates (template).
 * Shared by the API (validation at save time) and the worker (matching + interpolation).
 */

export const TRIGGER_TYPES = ['cron', 'interval', 'event', 'webhook'] as const;
export const TriggerType = z.enum(TRIGGER_TYPES);
export type TriggerType = z.infer<typeof TriggerType>;

/** Domain events an event trigger can listen to (the matcher handles exactly these). */
export const TRIGGER_EVENT_TYPES = [
  'run.completed',
  'run.failed',
  'task.done',
  'task.failed',
  'webhook.received',
  'agent.message',
] as const satisfies readonly DomainEventType[];
export type TriggerEventType = (typeof TRIGGER_EVENT_TYPES)[number];
export const isTriggerEventType = (t: string): t is TriggerEventType =>
  (TRIGGER_EVENT_TYPES as readonly string[]).includes(t);

/** Events whose matching triggers run "after" an agent finished (edges for the cycle check). */
export const AGENT_CHAIN_EVENT_TYPES: readonly TriggerEventType[] = [
  'run.completed',
  'run.failed',
  'task.done',
  'task.failed',
  'agent.message',
];

/** Internal outbox events (not user notifications). */
export const INTERNAL_EVENT_TYPES: ReadonlySet<string> = new Set([
  /** Durable completion hand-off: parent wake-up, task dependencies and report-back. */
  'run.coordination_requested',
  'trigger.changed',
  /** Worker-internal: run a vault sync now (knowledge.ts KNOWLEDGE_SYNC_EVENT). */
  'knowledge.sync_requested',
  /** Worker-internal: synthesize a recording into a flow (flow.ts FLOW_SYNTH_EVENT). */
  'recording.synthesize_requested',
  /** Worker-internal: a company was deleted → purge its runner resources (nobody is left to notify). */
  'org.deleted',
  /** Worker-internal: an idle agent was mentioned or DM'd → maybe a reply run (channel-wake.ts). */
  'channel.wake',
]);

const CRON_FIELD = /^[0-9A-Za-z*/,?#LW-]+$/;

/** True for a valid IANA time zone in this runtime. */
export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** 5-field cron (minute hour day-of-month month day-of-week); sub-minute patterns are rejected. */
export const CronSpec = z.object({
  pattern: z
    .string()
    .trim()
    .min(9)
    .max(120)
    .refine((p) => {
      const parts = p.split(/\s+/);
      return parts.length === 5 && parts.every((f) => CRON_FIELD.test(f));
    }, 'must be a 5-field cron pattern (minute hour day month weekday)'),
  tz: z.string().min(1).max(64).default('Europe/Amsterdam').refine(isValidTimeZone, 'unknown time zone'),
});
export type CronSpec = z.infer<typeof CronSpec>;

export const IntervalSpec = z.object({
  everyMs: z
    .number()
    .int()
    .min(60_000)
    .max(31 * 24 * 3600 * 1000),
});
export type IntervalSpec = z.infer<typeof IntervalSpec>;

export const EventSpec = z.object({
  event: z.enum(TRIGGER_EVENT_TYPES),
  /** Only events from this (source) agent. Omit = any agent (then the trigger's own agent matches too). */
  agentId: z.string().min(1).max(64).optional(),
  /** Run/task status of the source event (`completed`, `failed`, `done`). */
  status: z.string().min(1).max(40).optional(),
  /** All tags must be present in the event payload's `tags` array. */
  tags: z.array(z.string().min(1).max(40)).max(10).optional(),
});
export type EventSpec = z.infer<typeof EventSpec>;

export const WebhookSpec = z.object({}).strict();
export type WebhookSpec = z.infer<typeof WebhookSpec>;

export const TRIGGER_SPECS = {
  cron: CronSpec,
  interval: IntervalSpec,
  event: EventSpec,
  webhook: WebhookSpec,
} as const;
export type TriggerSpecFor<T extends TriggerType> = z.infer<(typeof TRIGGER_SPECS)[T]>;
export type TriggerSpec = CronSpec | IntervalSpec | EventSpec | WebhookSpec;

/** Parse a spec for the given trigger type (throws ZodError). */
export function parseTriggerSpec(type: TriggerType, spec: unknown): TriggerSpec {
  return TRIGGER_SPECS[type].parse(spec ?? {}) as TriggerSpec;
}

/**
 * Task created when the trigger fires. Placeholders: `{{event.summary}}`, `{{event.result}}`,
 * `{{event.error}}`, `{{event.title}}`, `{{event.agentId}}`, `{{event.taskId}}`, `{{event.runId}}`,
 * `{{event.type}}`, `{{trigger.name}}`, `{{now}}`, `{{payload}}` (webhook body). Values from
 * events and webhooks are untrusted and are inserted as labelled, size-limited data blocks.
 */
export const TaskTemplate = z.object({
  title: z.string().min(1).max(200),
  input: z.string().max(20_000).default(''),
  priority: z.number().int().min(1).max(10).default(5),
});
export type TaskTemplate = z.infer<typeof TaskTemplate>;

const TriggerLimits = {
  enabled: z.boolean().default(true),
  cooldownS: z
    .number()
    .int()
    .min(0)
    .max(7 * 24 * 3600)
    .default(0),
  maxFiresPerHour: z.number().int().min(1).max(3600).default(60),
  /** Admin-only: save an event trigger even though it closes an agent→agent cycle. */
  allowCycle: z.boolean().default(false),
};

const base = {
  agentId: z.string().min(1).max(64),
  name: z.string().min(1).max(80),
  taskTemplate: TaskTemplate,
  ...TriggerLimits,
};

export const TriggerInput = z.discriminatedUnion('type', [
  z.object({ ...base, type: z.literal('cron'), spec: CronSpec }),
  z.object({ ...base, type: z.literal('interval'), spec: IntervalSpec }),
  z.object({ ...base, type: z.literal('event'), spec: EventSpec }),
  z.object({ ...base, type: z.literal('webhook'), spec: WebhookSpec.default({}) }),
]);
export type TriggerInput = z.infer<typeof TriggerInput>;

/** Type and agent are fixed after creation; `spec` is validated against the existing type. */
export const TriggerPatch = z.object({
  name: z.string().min(1).max(80).optional(),
  spec: z.record(z.string(), z.unknown()).optional(),
  taskTemplate: TaskTemplate.optional(),
  enabled: z.boolean().optional(),
  cooldownS: TriggerLimits.cooldownS.unwrap().optional(),
  maxFiresPerHour: TriggerLimits.maxFiresPerHour.unwrap().optional(),
  allowCycle: z.boolean().optional(),
  /** Webhook triggers: generate a new signing secret (returned once in the response). */
  rotateWebhookSecret: z.boolean().optional(),
});
export type TriggerPatch = z.infer<typeof TriggerPatch>;

export const TriggerDto = z.object({
  id: z.string(),
  agentId: z.string(),
  name: z.string(),
  type: TriggerType,
  spec: z.record(z.string(), z.unknown()),
  taskTemplate: TaskTemplate,
  enabled: z.boolean(),
  cooldownS: z.number(),
  maxFiresPerHour: z.number(),
  allowCycle: z.boolean(),
  /** Set when the platform disabled the trigger (e.g. `circuit_breaker`). */
  disabledReason: z.string().nullable(),
  lastFiredAt: z.string().nullable(),
  /** Webhook triggers: path to POST to (relative to the app origin). */
  webhookPath: z.string().nullable(),
  /** Webhook triggers: "••••abcd" of the signing secret. */
  webhookSecretHint: z.string().nullable(),
  /** Only in the create response (and after `rotateWebhookSecret`): the signing secret, shown once. */
  webhookSecret: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TriggerDto = z.infer<typeof TriggerDto>;

export const TriggersQuery = z.object({
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  agentId: z.string().min(1).max(64).optional(),
  type: TriggerType.optional(),
});

export const TriggerFireDto = z.object({
  id: z.string(),
  triggerId: z.string(),
  source: z.string(),
  status: z.enum(['fired', 'skipped']),
  /** Skip reason: cooldown, max_fires_per_hour, chain_depth_exceeded, ancestry_limit, circuit_breaker, … */
  reason: z.string().nullable(),
  taskId: z.string().nullable(),
  runId: z.string().nullable(),
  sourceRunId: z.string().nullable(),
  createdAt: z.string(),
});
export type TriggerFireDto = z.infer<typeof TriggerFireDto>;

/** Manual test fire: optional fake event fields / payload used for interpolation. */
export const TriggerFireInput = z
  .object({
    event: z.record(z.string(), z.unknown()).optional(),
    payload: z.unknown().optional(),
    /** Durable delivery identity. Uses production guards; retrying reuses the same task/run. */
    deliveryKey: z
      .string()
      .regex(/^[a-zA-Z0-9:_./-]{1,200}$/)
      .optional(),
  })
  .default({});
export const TriggerFireResult = z.object({
  status: z.enum(['fired', 'skipped']),
  reason: z.string().nullable(),
  taskId: z.string().nullable(),
  runId: z.string().nullable(),
});
export type TriggerFireResult = z.infer<typeof TriggerFireResult>;

/** Response of the public webhook endpoint. */
export const WebhookAccepted = z.object({ accepted: z.literal(true), eventId: z.number() });

/** Webhook signing headers: `X-AF-Timestamp` (unix seconds) and `X-AF-Signature` (`sha256=<hex>`). */
export const WEBHOOK_HEADERS = { timestamp: 'x-af-timestamp', signature: 'x-af-signature' } as const;
export const WEBHOOK_TOLERANCE_S = 300;
export const WEBHOOK_MAX_BYTES = 64 * 1024;
